import React from 'react';
import ContactForm from './components/ContactForm';
import { motion } from 'framer-motion';
import { FaRocket, FaMobile, FaShoppingCart, FaStar, FaCheckCircle, FaClock, FaAward } from 'react-icons/fa';
import { useLanguage } from './LanguageContext';

function App() {
  const { currentLanguage, changeLanguage, t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://elixirblend.com/assets/images/logo.png" 
                alt="Elixir Blend Logo" 
                className="h-10 w-auto"
                onError={(e) => {
                  // Fallback to original design if logo fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-8 h-8 bg-gradient-orange rounded-lg flex items-center justify-center" style={{ display: 'none' }}>
                <span className="text-white font-bold text-lg">E</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* Language Selector */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`flex items-center space-x-1 px-2 py-1 rounded transition-colors ${
                    currentLanguage === 'en' ? 'bg-logo-orange text-white' : 'hover:bg-gray-100'
                  }`}
                  title="English"
                >
                  <span className="text-lg">🇬🇧</span>
                  <span className="text-sm font-medium hidden sm:inline">EN</span>
                </button>
                <button
                  onClick={() => changeLanguage('gr')}
                  className={`flex items-center space-x-1 px-2 py-1 rounded transition-colors ${
                    currentLanguage === 'gr' ? 'bg-logo-orange text-white' : 'hover:bg-gray-100'
                  }`}
                  title="Ελληνικά"
                >
                  <span className="text-lg">🇬🇷</span>
                  <span className="text-sm font-medium hidden sm:inline">GR</span>
                </button>
              </div>
              
              {/* Contact Info */}
              <div className="hidden md:flex items-center space-x-6">
                <span className="text-logo-orange font-semibold">{t('phone')}</span>
                <span className="text-gray-600">{t('email')}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Contact Form */}
      <section className="py-8 md:py-20 bg-gradient-to-br from-logo-orange-light via-white to-gray-50">
        {/* Mobile-first: Form appears first */}
        <div className="block md:hidden px-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-3xl shadow-2xl p-6 border border-gray-100"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-logo-black mb-3">
                {t('formTitle')}
              </h2>
              <p className="text-base text-gray-600">
                {t('formSubtitle')}
              </p>
            </div>
            <ContactForm />
          </motion.div>
        </div>

        <div className="container mx-auto px-6">
          <div className="text-center mb-8 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-logo-black mb-6">
                {t('heroTitle')}{' '}
                <span className="text-logo-orange">{t('heroTitleHighlight')}</span>
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                {t('heroSubtitle')}
              </p>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-8 md:mb-12">
                <div className="flex items-center space-x-2 bg-white px-3 md:px-4 py-2 rounded-full shadow-sm text-sm md:text-base">
                  <FaCheckCircle className="text-green-500" />
                  <span className="font-semibold text-gray-700">{t('websitesBuilt')}</span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-3 md:px-4 py-2 rounded-full shadow-sm text-sm md:text-base">
                  <FaClock className="text-logo-orange" />
                  <span className="font-semibold text-gray-700">{t('dayDelivery')}</span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-3 md:px-4 py-2 rounded-full shadow-sm text-sm md:text-base">
                  <FaAward className="text-purple-500" />
                  <span className="font-semibold text-gray-700">{t('clientRetention')}</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Contact Form - Desktop version */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden md:block bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-logo-black mb-4">
                {t('formTitle')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('formSubtitle')}
              </p>
            </div>
            <ContactForm />
          </motion.div>
        </div>
      </section>

      {/* Social Proof - Testimonials */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-logo-black mb-4">{t('testimonialsTitle')}</h2>
            <p className="text-lg md:text-xl text-gray-600">{t('testimonialsSubtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                name: "Maria Christofi",
                business: "Boutique Owner",
                text: "Elixir Blend created our online store in just 8 days! Sales increased by 150% in the first month. Absolutely amazing work!",
                rating: 5
              },
              {
                name: "Andreas Papadopoulos", 
                business: "Restaurant Chain",
                text: "Professional, fast, and exactly what we needed. Our online ordering system works perfectly and customers love the design.",
                rating: 5
              },
              {
                name: "Elena Georgiou",
                business: "Medical Practice",
                text: "The appointment booking system has streamlined our practice. Patients can now book online 24/7. Highly recommended!",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-xl" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-logo-black">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.business}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-logo-orange-light">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-logo-black mb-4">{t('servicesTitle')}</h2>
            <p className="text-lg md:text-xl text-gray-600">{t('servicesSubtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: <FaRocket className="text-3xl" />,
                title: t('businessWebsitesTitle'),
                description: t('businessWebsitesDesc')
              },
              {
                icon: <FaShoppingCart className="text-3xl" />,
                title: t('ecommerceStoresTitle'), 
                description: t('ecommerceStoresDesc')
              },
              {
                icon: <FaMobile className="text-3xl" />,
                title: t('mobileFirstTitle'),
                description: t('mobileFirstDesc')
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
              >
                <div className="text-logo-orange mb-4">{service.icon}</div>
                <h3 className="text-xl md:text-2xl font-bold text-logo-black mb-4">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-20 bg-gradient-orange text-white">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              {t('finalCtaTitle')}
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              {t('finalCtaText')}
            </p>
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-white text-logo-orange px-6 md:px-8 py-3 md:py-4 rounded-full font-bold text-base md:text-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              {t('finalCtaButton')}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-logo-black text-white py-8 md:py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-orange rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">E</span>
                </div>
                <span className="text-xl md:text-2xl font-bold">Elixir Blend</span>
              </div>
              <p className="text-gray-400 text-sm md:text-base">
                {t('footerDescription')}
              </p>
            </div>
            
            <div>
              <h3 className="text-lg md:text-xl font-bold mb-4">{t('contactInfo')}</h3>
              <div className="space-y-2 text-gray-400 text-sm md:text-base">
                <p>{t('phone')}</p>
                <p>✉️ {t('email')}</p>
                <p>🌐 elixirblend.com</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg md:text-xl font-bold mb-4">{t('quickStats')}</h3>
              <div className="space-y-2 text-gray-400 text-sm md:text-base">
                <p>🚀 {t('websitesBuilt')}</p>
                <p>{t('averageDelivery')}</p>
                <p>🏆 {t('clientRetention')}</p>
                <p>{t('starRating')}</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              {t('copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App; 